import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('Error caught by boundary:', error, errorInfo);
    
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Send to debug panel if available
    if (window.dispatchEvent) {
      const errorEvent = new CustomEvent('api-error', {
        detail: {
          timestamp: new Date().toISOString(),
          url: 'React Component',
          method: 'RENDER',
          status: 'ERROR',
          message: error.toString(),
          data: {
            componentStack: errorInfo.componentStack,
            errorCount: this.state.errorCount + 1
          }
        }
      });
      window.dispatchEvent(errorEvent);
    }
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Check if too many errors (possible infinite loop)
      if (this.state.errorCount > 3) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 p-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">
                Critical Error
              </h1>
              <p className="text-gray-600 text-center mb-6">
                The application has encountered repeated errors. Please reload the page.
              </p>
              <Button
                onClick={this.handleReload}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Application
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">
              Something went wrong
            </h1>
            
            <p className="text-gray-600 text-center mb-6">
              The application encountered an unexpected error. You can try to continue or reload the page.
            </p>

            <div className="space-y-3 mb-6">
              <Button
                onClick={this.handleReset}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Try to Continue
              </Button>
              
              <Button
                onClick={this.handleReload}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
            </div>

            {/* Error details for debugging (collapsible) */}
            {this.state.error && (
              <details className="mt-6">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  Error Details (for debugging)
                </summary>
                <div className="mt-2 p-4 bg-gray-50 rounded border border-gray-200 text-xs font-mono overflow-auto max-h-64">
                  <div className="text-red-600 mb-2">
                    <strong>Error:</strong> {this.state.error.toString()}
                  </div>
                  {this.state.errorInfo && (
                    <div className="text-gray-600">
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
